import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight, MapPin, Bed, Bath, Maximize, Phone, ExternalLink, Building2 } from 'lucide-react';
import { MessageIcon } from '@/components/property/ContactIcons';
import type { StoryWithImages } from '@/hooks/useStories';
import { useTranslation } from 'react-i18next';
import { pickLocalized } from '@/hooks/useLandingContent';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice } from '@/lib/formatPrice';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { CallContactDialog } from '@/components/property/CallContactDialog';
import { EmailContactDialog } from '@/components/property/EmailContactDialog';
import { SwipeableZoomImage } from '@/components/ui/swipeable-zoom-image';

const DURATION_MS = 3000;

interface Props {
  stories: StoryWithImages[];
  startIndex: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StoryViewerDialog({ stories, startIndex, open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { displayCurrency, rate } = useCurrency();
  const lang = i18n.language || 'en';
  const [storyIdx, setStoryIdx] = useState(startIndex);
  const [imgIdx, setImgIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [callOpen, setCallOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startTsRef = useRef<number>(0);

  useEffect(() => { if (open) { setStoryIdx(startIndex); setImgIdx(0); setProgress(0); } }, [open, startIndex]);

  useEffect(() => {
    if (!open || callOpen || emailOpen) return;
    const story = stories[storyIdx];
    if (!story?.story_images.length) return;

    startTsRef.current = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTsRef.current;
      const p = Math.min(1, elapsed / DURATION_MS);
      setProgress(p);
      if (p >= 1) {
        // advance image; pause on last frame
        const story = stories[storyIdx];
        if (imgIdx + 1 < story.story_images.length) {
          setImgIdx(imgIdx + 1); setProgress(0);
        } else {
          setProgress(1);
        }
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line
  }, [open, storyIdx, imgIdx, callOpen, emailOpen]);

  const nextImage = () => {
    const story = stories[storyIdx];
    if (!story) return;
    if (imgIdx + 1 < story.story_images.length) {
      setImgIdx(imgIdx + 1); setProgress(0);
    }
  };
  const prevImage = () => {
    if (imgIdx > 0) { setImgIdx(imgIdx - 1); setProgress(0); }
  };
  const nextStory = () => {
    if (storyIdx + 1 < stories.length) { setStoryIdx(storyIdx + 1); setImgIdx(0); setProgress(0); }
  };
  const prevStory = () => {
    if (storyIdx > 0) { setStoryIdx(storyIdx - 1); setImgIdx(0); setProgress(0); }
  };

  const story = stories[storyIdx];
  if (!story) return null;
  const img = story.story_images[imgIdx];
  const title = pickLocalized(story.title, lang);
  const desc = pickLocalized(story.description, lang);
  const location = [story.sub_city, story.city].filter(Boolean).join(', ');
  const isRent = story.listing_type === 'rent';
  const refId = story.reference_code || story.id.slice(0, 8).toUpperCase();
  const goDetails = () => { onOpenChange(false); navigate(`/property/${story.slug || story.id}`); };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md p-0 bg-black border-0 overflow-hidden h-[85vh] sm:h-[90vh]">
          <div className="relative w-full h-full flex items-center justify-center">
            {story.story_images.length > 0 && (
              <div className="absolute inset-0">
                <SwipeableZoomImage
                  images={story.story_images.map((si) => optimizedImageUrl(si.url, { width: 800, height: 1200, quality: 85 }))}
                  index={imgIdx}
                  onIndexChange={(i) => { setImgIdx(i); setProgress(0); }}
                  alt={title}
                  loop={false}
                  imageClassName="!max-w-none w-full h-full object-cover"
                />
              </div>
            )}

            {/* Progress bars */}
            <div className="absolute top-2 left-2 right-2 flex gap-1 z-20">
              {story.story_images.map((_, i) => (
                <div key={i} className="flex-1 h-1 bg-white/30 rounded overflow-hidden">
                  <div
                    className="h-full bg-white transition-[width] duration-75"
                    style={{ width: i < imgIdx ? '100%' : i === imgIdx ? `${progress * 100}%` : '0%' }}
                  />
                </div>
              ))}
            </div>

            {/* Header */}
            <div className="absolute top-5 left-3 right-3 flex items-start justify-between z-20 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-9 w-9 rounded-full bg-white flex items-center justify-center shrink-0 overflow-hidden border border-white/40">
                  {story.agency_logo_url ? (
                    <img src={story.agency_logo_url} alt={story.agency_name || 'Agency'} className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="h-4 w-4 text-primary" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-white text-sm font-semibold truncate flex items-center gap-1.5">
                    <span className="truncate">{title}</span>
                    <span className="px-1.5 py-0.5 bg-white/20 text-white text-[10px] rounded uppercase shrink-0">
                      For {isRent ? 'Rent' : 'Sale'}
                    </span>
                  </div>
                  {location && (
                    <div className="text-white/80 text-xs flex items-center gap-1 truncate">
                      <MapPin className="h-3 w-3 shrink-0" /> {location}
                    </div>
                  )}
                </div>
              </div>
              <button onClick={() => onOpenChange(false)} className="text-white p-1 hover:bg-white/10 rounded-full shrink-0">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Swipe gestures on the image handle image navigation; pinch/double-tap zoom. */}

            {/* Side arrows: between stories */}
            <button onClick={prevStory} disabled={storyIdx === 0} aria-label="Previous story"
              className="absolute -left-1 sm:left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 hover:bg-white items-center justify-center z-20 flex disabled:opacity-40 disabled:pointer-events-none shadow">
              <ChevronLeft className="h-5 w-5 text-foreground" />
            </button>
            <button onClick={nextStory} disabled={storyIdx === stories.length - 1} aria-label="Next story"
              className="absolute -right-1 sm:right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 hover:bg-white items-center justify-center z-20 flex disabled:opacity-40 disabled:pointer-events-none shadow">
              <ChevronRight className="h-5 w-5 text-foreground" />
            </button>

            {/* Bottom info card (gradient passes touches through; controls stay interactive) */}
            <div className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black via-black/85 to-transparent pt-10 pb-3 px-4 text-white pointer-events-none">
              <div className="text-lg font-bold mb-1">
                {formatPrice(story.price ?? 0, story.currency || 'ETB', displayCurrency, rate)}
              </div>
              <div className="flex items-center gap-3 text-xs text-white/90 mb-2">
                {story.bedrooms != null && (
                  <span className="flex items-center gap-1"><Bed className="h-3.5 w-3.5" /> {story.bedrooms}</span>
                )}
                {story.bathrooms != null && (
                  <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" /> {story.bathrooms}</span>
                )}
                {story.area_sqm != null && (
                  <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5" /> {Math.round(story.area_sqm)} {story.area_unit || 'm²'}</span>
                )}
              </div>
              {desc && (
                <p className="text-xs text-white/80 line-clamp-2 mb-2 pointer-events-auto">
                  {desc}{' '}
                  <button onClick={goDetails} className="text-primary-foreground underline underline-offset-2 font-medium">
                    View Details
                  </button>
                </p>
              )}
              <div className="grid grid-cols-2 gap-2 mb-2 pointer-events-auto">
                <Button
                  onClick={() => setCallOpen(true)}
                  className="h-11 gap-2 font-semibold text-sm bg-primary hover:bg-primary/90 text-primary-foreground"
                  aria-label="Call"
                >
                  <Phone className="h-4 w-4" />
                  <span>Call</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEmailOpen(true)}
                  className="h-11 gap-2 font-semibold text-sm bg-white text-primary border-primary/30 hover:bg-white/90"
                  aria-label="Message"
                >
                  <MessageIcon className="h-4 w-4" />
                  <span>Message</span>
                </Button>
              </div>
              <button onClick={goDetails} className="w-full text-center text-xs font-semibold text-white/95 flex items-center justify-center gap-1 hover:text-white pointer-events-auto">
                <ExternalLink className="h-3 w-3" /> VIEW PROPERTY DETAILS
              </button>
            </div>

          </div>
        </DialogContent>
      </Dialog>

      <CallContactDialog
        open={callOpen}
        onOpenChange={setCallOpen}
        agentName={story.contact_person || 'Property Agent'}
        mobileNumber={story.contact_phone || undefined}
        referenceId={refId}
        listingId={story.id}
        listingType="property"
      />
      <EmailContactDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        agentName={story.contact_person || 'Property Agent'}
        phoneNumber={story.contact_phone || undefined}
        referenceId={refId}
        propertyId={story.id}
        listingId={story.id}
        listingType="property"
      />
    </>
  );
}
